import { useGetCurrentAgentsPresenter } from '@/pages/agents/hooks';
import { cropPresenterAvatar } from '@/services/images/crop.service';
import { isImageProcessing } from '@/state/presenters/presenters';
import { useAgentEditorStore } from '@/state/root';
import theme from '@/theme';
import { VideoType } from '@d-id/types';
import styled from '@emotion/styled';
import { Avatar } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import Lottie from 'react-lottie';
import { useRecoilValue } from 'recoil';
import LoadingAnimation from '../../../components/loaderSmallLottie.json';
import { StitchButtons } from './StitchButtons';

const StyledAvatarContainer = styled.div`
    position: relative;
    width: 250px;
    height: 250px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
`;

const StyledAvatar = styled(Avatar, {
    shouldForwardProp: prop => prop !== 'isUploading',
})<{ isUploading?: boolean }>`
    width: 100%;
    height: 100%;
    background: ${theme.colors.black[200]};
    opacity: ${({ isUploading }) => (isUploading ? 0.3 : 1)};
    border-radius: 16px;
`;

const Loader = styled.div<{ show: boolean }>`
    display: ${({ show }) => (show ? 'block' : 'none')};
    position: absolute;
    top: 90px;
    left: 90px;
    background: transparent;
    z-index: 1;
`;

const StitchButtonsContainer = styled.div`
    position: absolute;
    left: 24px;
    margin-bottom: 12px;
`;

const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: LoadingAnimation,
    rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
    },
};

interface Props {
    enableStitchSelection?: boolean;
}

export const AgentAvatar = observer(({ enableStitchSelection }: Props) => {
    const agentEditorStore = useAgentEditorStore();

    const presenter = useGetCurrentAgentsPresenter();
    const isUploading = useRecoilValue(isImageProcessing);
    const [croppedAvatarSrc, setCroppedAvatarSrc] = useState<string | null>(null);

    const avatarSrc = agentEditorStore.presenterBackgroundThumbnailUrl || presenter?.thumbnail || presenter?.poster;

    useEffect(() => {
        if (!avatarSrc) {
            return;
        }

        setCroppedAvatarSrc(null);
        cropPresenterAvatar({
            imgUrl: avatarSrc,
            shouldSkipDetection: presenter?.type !== VideoType.TALK || agentEditorStore.presenterIsStitch,
        })
            .then(setCroppedAvatarSrc)
            .catch(() => {
                setCroppedAvatarSrc(null);
            });
    }, [avatarSrc, presenter, agentEditorStore.presenterIsStitch]);

    return (
        <StyledAvatarContainer>
            <Loader show={isUploading}>
                <Lottie options={defaultOptions} height={70} width={70} />{' '}
            </Loader>
            <StyledAvatar isUploading={isUploading} alt="Your Agent" src={croppedAvatarSrc || avatarSrc} />
            {enableStitchSelection && !isUploading && (
                <StitchButtonsContainer>
                    <StitchButtons hideTooltip />
                </StitchButtonsContainer>
            )}
        </StyledAvatarContainer>
    );
});
